import {
  saveResult as saveToLocalStorage,
  getResult as getFromLocalStorage,
  clearResult as clearFromLocalStorage,
} from '../util/mockup-manager';
import { updateRoomMockup as updateRoomMockupAction } from '../ducks/user.duck';

/**
 * Unified mockup storage service that automatically selects storage backend
 * based on authentication state. Provides consistent API for all mockup operations.
 */
class MockupStorageService {
  constructor(isAuthenticated, dispatch, currentUser) {
    this.isAuthenticated = isAuthenticated;
    this.dispatch = dispatch;
    this.currentUser = currentUser;
  }

  /**
   * Get mockup data from appropriate storage
   * @returns {Promise<Object|null>} Mockup data or null if not found
   */
  async get() {
    if (this.isAuthenticated) {
      return this._getFromAPI();
    } else {
      return this._getFromLocalStorage();
    }
  }

  /**
   * Save mockup data to appropriate storage
   * @param {Object} data - Mockup data to save
   * @param {string} jobId - Optional job ID for localStorage tracking
   * @returns {Promise<Object>} Saved mockup data
   */
  async save(data, jobId = null) {
    if (this.isAuthenticated) {
      return this._saveToAPI(data);
    } else {
      return this._saveToLocalStorage(data, jobId);
    }
  }

  /**
   * Update existing mockup data
   * @param {Object} data - Updated mockup data
   * @returns {Promise<Object>} Updated mockup data
   */
  async update(data) {
    if (this.isAuthenticated) {
      return this._saveToAPI(data);
    } else {
      return this._saveToLocalStorage(data);
    }
  }

  /**
   * Delete mockup data from appropriate storage
   * @returns {Promise<void>}
   */
  async delete() {
    if (this.isAuthenticated) {
      return this._deleteFromAPI();
    } else {
      return this._deleteFromLocalStorage();
    }
  }

  // Private methods for API operations

  _getFromAPI() {
    const userMockup = this.currentUser?.attributes?.profile?.privateData?.roomMockup;
    return Promise.resolve(userMockup || null);
  }

  async _saveToAPI(data) {
    if (!this.dispatch) {
      throw new Error('Dispatch function is required for API operations');
    }
    await this.dispatch(updateRoomMockupAction(data));
    return data;
  }

  async _deleteFromAPI() {
    if (!this.dispatch) {
      throw new Error('Dispatch function is required for API operations');
    }
    await this.dispatch(updateRoomMockupAction(null));
  }

  // Private methods for localStorage operations

  _getFromLocalStorage() {
    const localMockup = getFromLocalStorage();
    return Promise.resolve(localMockup || null);
  }

  _saveToLocalStorage(data, jobId = null) {
    saveToLocalStorage(jobId, data);
    return Promise.resolve(data);
  }

  _deleteFromLocalStorage() {
    clearFromLocalStorage();
    return Promise.resolve();
  }
}

/**
 * Factory function to create MockupStorageService instance
 * @param {Object} options
 * @param {boolean} options.isAuthenticated - Whether user is authenticated
 * @param {Function} options.dispatch - Redux dispatch function (required for authenticated users)
 * @param {Object} options.currentUser - Current user object (required for authenticated users)
 * @returns {MockupStorageService}
 */
export const createMockupStorage = ({ isAuthenticated, dispatch, currentUser }) => {
  return new MockupStorageService(isAuthenticated, dispatch, currentUser);
};

export default MockupStorageService;
